'use client'

import { datadogLogs } from '@datadog/browser-logs'
// NB: We could use @datadog/browser-rum/slim if we didn't need session replay
import { datadogRum } from '@datadog/browser-rum'

import {
    isAmplitudeFetchNoise,
    isCrossOriginScriptError,
    isHeadlessBot,
    isOutlookSafeLinksScanner,
    isThirdPartyAuthNoise,
} from './rum-noise'
import type { DatadogClient, DatadogInitConfig } from './types'

export { initializeClientLogger } from './logger/client'

/**
 * First-party analytics beacons — Snowplow (`sp`) and Amplitude events (`an`) /
 * remote-config (`an-config`). All are fire-and-forget POSTs; when an ad/tracker
 * blocker or a Cloudflare bot challenge blocks them the request never completes
 * (`status_code: 0`), so the failure is silently lost and not actionable. These
 * are dropped in beforeSend; real server errors (4xx/5xx) still report.
 */
const BLOCKED_ANALYTICS_HOSTS = ['sp.wanderu.com', 'an.wanderu.com', 'an-config.wanderu.com']

export const initializeDatadog = (config: DatadogInitConfig): DatadogClient => {
    const logsEnabled = config.datadog.logs.enabled
    const rumEnabled = config.datadog.rum.enabled

    if (logsEnabled) {
        datadogLogs.init({
            clientToken: config.datadog.clientToken,
            site: config.datadog.site,
            service: config.service.name,
            env: config.env,
            version: config.service.version,
            sessionSampleRate: config.datadog.logs.sessionSampleRate,
            forwardErrorsToLogs: config.datadog.logs.forwardErrorsToLogs,
            telemetrySampleRate: config.datadog.logs.telemetrySampleRate,
            silentMultipleInit: true, //NB: Multiple init is just a no-op
            beforeSend: (log) => {
                // First-party analytics beacons (Snowplow + Amplitude) blocked
                // client-side report status 0 (no response) — fire-and-forget and
                // not actionable. Cloudflare bot challenges and ad/tracker blockers
                // are the usual cause. Real server errors (4xx/5xx) still report.
                if (
                    log.http?.status_code === 0 &&
                    BLOCKED_ANALYTICS_HOSTS.some((host) => log.http?.url?.includes(host))
                ) {
                    return false
                }

                // Downgrade cross-origin "Script error." entries to warn — unactionable
                // without a stack trace, but worth keeping for visibility. Matches both
                // the raw and SDK-wrapped forms (see isCrossOriginScriptError).
                if (log.status === 'error' && isCrossOriginScriptError(log.message)) {
                    log.status = 'warn'
                }

                // Drop known Google One Tap / FedCM console noise
                // outright — benign, user-driven third-party auth-SDK errors we can't
                // fix from our code and have no reason to keep. This is a curated
                // allowlist of confirmed-unuseful patterns (see isThirdPartyAuthNoise),
                // so dropping is safe; an unrecognized auth error is deliberately NOT
                // matched and keeps its error status so it still surfaces.
                if (log.status === 'error' && isThirdPartyAuthNoise(log.message)) {
                    return false
                }

                // Drop the Amplitude SDK's own console.error for blocked analytics
                // fetches (paired with the status-0 network logs dropped above) —
                // not actionable. Narrowly matched so genuine SDK errors still surface.
                if (log.status === 'error' && isAmplitudeFetchNoise(log.message)) {
                    return false
                }

                // Returning undefined is functionally equivalent to true for Datadog's beforeSend;
                // cast required because the SDK types declare boolean return, not boolean | undefined
                return true
            },
        })
    }

    if (rumEnabled) {
        datadogRum.init({
            applicationId: config.datadog.appId,
            clientToken: config.datadog.clientToken,
            site: config.datadog.site,
            service: config.service.name,
            env: config.env,
            version: config.service.version,
            allowedTracingUrls: [
                {
                    // NB: Ignore local-dev-only nextjs resources
                    match: (url: string) =>
                        !url.includes('__nextjs') && !url.includes('/_next/static/webpack/'),
                    propagatorTypes: ['tracecontext'],
                },
            ],
            sessionSampleRate: config.datadog.rum.sessionSampleRate,
            sessionReplaySampleRate: config.datadog.rum.sessionReplaySampleRate,
            traceSampleRate: config.datadog.rum.traceSampleRate,
            traceContextInjection: config.datadog.rum.traceContextInjection,
            trackUserInteractions: config.datadog.rum.trackUserInteractions,
            trackResources: config.datadog.rum.trackResources,
            trackLongTasks: config.datadog.rum.trackLongTasks,
            defaultPrivacyLevel: config.datadog.rum.defaultPrivacyLevel,
            telemetrySampleRate: config.datadog.rum.telemetrySampleRate,
            silentMultipleInit: true, // NB: Multiple init is just a no-op
            beforeSend: (event) => {
                if (isHeadlessBot()) {
                    return false
                }

                if (event.type === 'error') {
                    const message = event.error?.message
                    if (isCrossOriginScriptError(message)) {
                        return false
                    }

                    if (isOutlookSafeLinksScanner(message)) {
                        return false
                    }
                }

                return true
            },
        })
    }

    return {
        // User & Consent Management (work with both RUM and Logs)
        setTrackingConsent: (consent) => {
            if (rumEnabled) {
                datadogRum.setTrackingConsent(consent)
            }
            if (logsEnabled) {
                datadogLogs.setTrackingConsent(consent)
            }
        },
        setUser: (user) => {
            // Map visitorId to id (required by Datadog SDK)
            const { visitorId, ...rest } = user
            const ddUser = { id: visitorId, ...rest }
            if (rumEnabled) {
                datadogRum.setUser(ddUser)
            }
            if (logsEnabled) {
                datadogLogs.setUser(ddUser)
            }
        },
        setUserProperty: (key, value) => {
            if (rumEnabled) {
                datadogRum.setUserProperty(key, value)
            }
            if (logsEnabled) {
                datadogLogs.setUserProperty(key, value)
            }
        },
        clearUser: () => {
            if (rumEnabled) {
                datadogRum.clearUser()
            }
            if (logsEnabled) {
                datadogLogs.clearUser()
            }
        },

        // Error Tracking (RUM-only)
        addError: (error) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.addError(error)
        },

        // Custom Metrics & Events (RUM-only)
        addAction: (name, context) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.addAction(name, context as never)
        },
        addTiming: (name, duration) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.addTiming(name, duration)
        },
        addFeatureFlag: (key, value) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.setGlobalContextProperty(`feature_flags.${key}`, value)
        },

        // Global Context Management (RUM-only)
        setGlobalContext: (context) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.setGlobalContext(context as never)
        },
        setGlobalContextProperty: (key, value) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.setGlobalContextProperty(key, value)
        },
        removeGlobalContextProperty: (key) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.removeGlobalContextProperty(key)
        },

        // Session & View Management (RUM-only)
        getInternalContext: () => {
            if (!rumEnabled) {
                return undefined as never
            }
            return datadogRum.getInternalContext() as never
        },
        startView: (name, context) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.startView(name)
            if (context) {
                Object.entries(context).forEach(([key, value]) => {
                    datadogRum.setGlobalContextProperty(`view.${key}`, value)
                })
            }
        },
    }
}
